import React from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Calendar, Bell, HeartPulse, Settings,
  LogOut, User, Heart
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import kivoIcon from '../../assets/logo/kivo-icon-only.png';

/**
 * Parent Sidebar - Kivo Family Navigation
 * Warm, calm design with 5 core sections
 */
export const ParentSidebar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const familyId = user?.familyId || localStorage.getItem('family_id');
  // Prefer real name from login response; fall back to localStorage; fall back to email prefix
  const displayName = user?.fullName
    || localStorage.getItem('parent_name')
    || (() => {
      try {
        const token = localStorage.getItem('access_token');
        if (!token) return null;
        const payload = JSON.parse(atob(token.split('.')[1]));
        const email = payload.sub || payload.email || '';
        const namePart = email.split('@')[0];
        return namePart.charAt(0).toUpperCase() + namePart.slice(1);
      } catch { return null; }
    })();

  const navItems = [
    { to: `/parent?family_id=${familyId}`, icon: Calendar, label: 'Today', exact: true },
    { to: `/parent/updates?family_id=${familyId}`, icon: Bell, label: 'Updates' },
    { to: `/parent/care?family_id=${familyId}`, icon: HeartPulse, label: 'Care' },
    { to: `/parent/settings?family_id=${familyId}`, icon: Settings, label: 'Settings' },
  ];

  const NavItem = ({ to, icon: Icon, label, exact }) => {
    const isActive = exact
      ? location.pathname === '/parent' && !location.pathname.includes('/parent/')
      : location.pathname.startsWith(to.split('?')[0]);

    return (
      <NavLink
        to={to}
        data-testid={`nav-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
        className={`flex items-center gap-3 px-4 py-2.5 rounded-xl font-medium transition-all text-sm ${
          isActive
            ? 'bg-primary text-white shadow-sm'
            : 'text-slate-600 hover:bg-primary-50 hover:text-primary'
        }`}
      >
        <Icon className="w-4.5 h-4.5 flex-shrink-0" style={{ width: '1.125rem', height: '1.125rem' }} />
        {label}
      </NavLink>
    );
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="hidden md:flex w-64 bg-white border-r border-slate-100 h-screen sticky top-0 flex-col" data-testid="parent-sidebar">
      {/* Logo */}
      <div className="p-6 pb-4">
        <div className="flex items-center gap-3">
          <img src={kivoIcon} alt="Kivo" className="h-8 w-auto flex-shrink-0" />
          <div>
            <h1 className="text-lg font-bold text-slate-800 tracking-tight font-heading">Kivo</h1>
            <p className="text-[10px] text-slate-400 -mt-0.5 font-sans">Your family companion</p>
          </div>
        </div>
      </div>

      {/* Divider */}
      <div className="mx-5 h-px bg-slate-100 mb-3" />

      {/* User Info */}
      <div className="px-3 mb-3">
        <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl bg-primary-50">
          <div className="w-8 h-8 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
            <User className="w-4 h-4 text-primary" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700 truncate">{displayName || 'My Family'}</p>
            <p className="text-xs text-slate-400">Parent</p>
          </div>
        </div>
      </div>

      <nav className="flex-1 px-3 space-y-0.5">
        {navItems.map((item) => (
          <NavItem key={item.to} {...item} />
        ))}
      </nav>

      {/* Footer */}
      <div className="p-4 border-t border-slate-100">
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 px-4 py-2 w-full text-slate-400 hover:text-slate-600 hover:bg-slate-50 rounded-xl text-sm transition-colors"
          data-testid="parent-logout"
        >
          <LogOut className="w-4 h-4" />
          Sign out
        </button>
      </div>
    </div>
  );
};

export default ParentSidebar;
